import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { lockClick } from 'directives/cs-capture-cover/cs-capture-cover.directive';
import { LL } from 'i18n/i18n.service';
import { untranslatedString } from 'models/translated-string';
import { CsPage, NavController } from 'providers/nav-controller';
import { ModalPageMarker } from 'providers/modal-page-marker';
import { AuthService } from 'services/auth.service';
import { CouchLoginReason } from 'services/couch-login-storm';
import { CloudFunctionsService, CloudFunctionHttpError } from 'services/cloud-functions.service';
import { AlertController, AlertButtonRole } from 'providers/alert-controller';
import { sleep } from '@cf-shared/shared-util';
import { AuthRole } from '@cf-shared/couch-auth';
import { LoadingController } from 'providers/loading-controller';
import { MenuController } from 'providers/menu-controller';
import { getMyApp } from 'app/app-interface';
import { LoggingService } from 'services/logging.service';

@Component({
    selector: 'page-redeem-code',
    templateUrl: 'redeem-code.html',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [ModalPageMarker]
})
export class RedeemCodePage extends CsPage<{}> {
  protected LL = LL;
  protected email: string;
  protected key = '';
  protected message = '';

  constructor(
    public navCtrl: NavController,
    protected auth: AuthService,
    protected loadingCtrl: LoadingController,
    protected cloudFunctions: CloudFunctionsService,
    protected alertCtrl: AlertController,
    private menuCtrl: MenuController,
    private cdRef: ChangeDetectorRef,
    private logging: LoggingService,
  ) {
    super();
  }

  async ionViewCanEnter() {
    this.email = this.auth.user.email;
    this.cdRef.markForCheck();
  }

  async ionViewWillEnter() {
    this.menuCtrl.enable(false);
  }

  async ionViewWillLeave() {
    this.menuCtrl.enable(true);
  }

  onCancelButtonClick(event: Event) {
    lockClick(event, this.navCtrl.popAsync());
  }

  handleRedeem() {
    void this.redeemCodeAsync();
  }
  async redeemCodeAsync() {
    let loading = this.loadingCtrl.create({ content: LL.redeemCode.loading.redeemingCode() });
    await loading.present();
    // this prevents menu items from re-querying and therefore wrongly caching the auth
    await getMyApp().markAppLoadingAsync(true);

    let error: string;
    try {
      let result = await this.cloudFunctions.useActivationKeyAsync({ code: this.key });
      error = result.error;

      if (!error) {
        // wait a bit to give time to UI to show the loadingCtrl, so that the refresh doesn't look weird
        let [loginSuccess] = await Promise.all([this.auth.attemptCouchLoginAsync(CouchLoginReason.RedeemCode), sleep(1000)]);

        if (!loginSuccess) {
          error = LL.redeemCode.errors.loginFailed();
        } else if (this.auth.getAuthRole() < AuthRole.Paid) {
          error = LL.redeemCode.errors.activationFailed();
        }
      }
    } catch (err) {
      // #4283: useActivationKeyAsync can now THROW instead of always returning
      // { error } — a version-affinity refusal (this server is older than the
      // account's data) surfaces as an HTTP 409, and any other unexpected server
      // failure can throw too. Unexpected conditions must surface to the user
      // (CLAUDE.md), never leave the loading spinner stuck forever, so this is
      // caught here and turned into the same kind of message the rest of this
      // function already shows through an alert.
      if (err instanceof CloudFunctionHttpError && err.httpStatus === 409) {
        // this specific, known, recoverable condition (this server is behind
        // this account's data version) already has its own honest message —
        // deliberately not sent to Sentry, matching the choice already made
        // for the same condition in auth.service.ts / online-state.service.ts,
        // where a later couch-login attempt is what reports it to Mixpanel
        // (couch_login_failure / ServerDataVersionMismatch). This code path
        // does NOT itself trigger a couch-login attempt, so a console.warn is
        // the only trace this specific activation-time occurrence leaves.
        console.warn('RedeemCodePage: activation failed — this server is behind this account\'s data version', err);
        error = LL.app.serverBehindAccountVersion();
      } else {
        // anything else here is a genuinely unexpected failure (a real server
        // error, a network failure that escaped the cloud-functions retry
        // wrapper, …) — must surface to Sentry, not just the console, or a
        // future debugger has no record of why activation failed.
        this.logging.trackAbnormalException(err);
        error = LL.redeemCode.errors.activationFailed();
      }
    }

    await loading.dismiss();
    await getMyApp().markAppLoadingAsync(false);

    if (error) {
      this.message = error;
      this.cdRef.markForCheck();
      await this.alertCtrl.showAlertAsync({
        message: untranslatedString(error),
        enableBackdropDismiss: true,
        buttons: [
          {
            text: LL.common.buttons.ok(),
            role: AlertButtonRole.Primary,
          },
        ],
      });
      console.log(this.message);
    } else {
      this.cdRef.markForCheck();
      await this.alertCtrl.showAlertAsync({
        message: LL.redeemCode.alerts.successfullyRedeemed(),
        enableBackdropDismiss: true,
        buttons: [
          {
            text: LL.common.buttons.ok(),
            role: AlertButtonRole.Primary,
          },
        ],
      });

      // redirect the app where needed and hide the loading screen
      await getMyApp().gotoDefaultPageAsync();
    }
  }
}
